import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Stack, Card, Text, Badge, Group, Title, Accordion, Paper, List, ThemeIcon, Alert, Box } from '@mantine/core';
import { IconShield, IconAlertTriangle, IconChecklist, IconInfoCircle } from '@tabler/icons-react';

/**
 * SecurityTabPanel - Display security threat scenarios and controls
 * Shows scenarios generated by autopilot with their threat assessments and mitigation controls
 */
export default function SecurityTabPanel({ modelId }) {
  const securityState = useSelector((state) => state.security.models[modelId] || {});

  // Memoize scenarios and controls to prevent re-renders
  const scenarios = useMemo(() => securityState.scenarios || {}, [securityState.scenarios]);
  const securityControls = useMemo(() => securityState.securityControls || {}, [securityState.securityControls]);

  // Convert scenarios object to sorted array
  const scenarioList = useMemo(() => {
    return Object.entries(scenarios)
      .map(([scenarioId, scenario]) => ({
        scenarioId,
        ...scenario
      }))
      .sort((a, b) => {
        // Sort by updatedAt (newest first)
        const dateA = new Date(a.updatedAt || 0);
        const dateB = new Date(b.updatedAt || 0);
        return dateB - dateA;
      });
  }, [scenarios]);

  // Statistics
  const stats = useMemo(() => {
    const controlsCount = Object.values(securityControls).reduce((sum, controls) => {
      return sum + (Array.isArray(controls) ? controls.length : 0);
    }, 0);

    return {
      totalScenarios: scenarioList.length,
      totalControls: controlsCount,
      activeScenarios: scenarioList.filter(s => s.status === 'active').length
    };
  }, [scenarioList, securityControls]);

  // Helper to get threat level color
  const getThreatLevelColor = (level) => {
    const levelLower = (level || '').toLowerCase();
    if (levelLower === 'critical') return 'red';
    if (levelLower === 'high') return 'orange';
    if (levelLower === 'moderate') return 'yellow';
    if (levelLower === 'low') return 'blue';
    return 'gray';
  };

  // Empty state
  if (scenarioList.length === 0) {
    return (
      <Stack gap="lg" align="center" py="xl">
        <IconShield size={64} color="gray" opacity={0.3} />
        <Stack gap="xs" align="center">
          <Title order={3} c="dimmed">No Security Scenarios</Title>
          <Text size="sm" c="dimmed" ta="center" maw={500}>
            Security threat scenarios will appear here after running an autopilot assessment.
            The security phase analyzes your model for potential security threats and generates mitigation controls.
          </Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header with statistics */}
      <Card withBorder radius="md" p="lg" bg="blue.0">
        <Group gap="xl" justify="space-between" wrap="wrap">
          <Group gap="md">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconShield size={20} />
            </ThemeIcon>
            <Stack gap={4}>
              <Text size="lg" fw={600}>Security Threat Assessment</Text>
              <Text size="xs" c="dimmed">AI-generated threat scenarios and mitigation controls</Text>
            </Stack>
          </Group>

          <Group gap="xl">
            <Stack gap={4} align="center">
              <Badge variant="filled" color="blue" size="lg" radius="md">
                {stats.totalScenarios}
              </Badge>
              <Text size="xs" c="dimmed">Scenarios</Text>
            </Stack>

            <Stack gap={4} align="center">
              <Badge variant="filled" color="green" size="lg" radius="md">
                {stats.totalControls}
              </Badge>
              <Text size="xs" c="dimmed">Controls</Text>
            </Stack>
          </Group>
        </Group>
      </Card>

      {/* Scenarios Accordion */}
      <Accordion variant="separated" radius="md">
        {scenarioList.map((scenario) => {
          const controls = securityControls[scenario.scenarioId] || [];
          const attackSurface = scenario.attackSurface || {};
          const impact = scenario.impact || {};
          const assessmentData = scenario.assessmentData || {};

          // Extract threat assessment from assessmentData
          const threatAssessment = assessmentData['Threat Assessment'] || {};
          const threatLevel = threatAssessment.threatLevel || 'Unknown';
          const vulnerabilityLevel = threatAssessment.vulnerabilityLevel;
          const assetValue = threatAssessment.assetValue;
          const likelihood = threatAssessment.likelihood;
          const severity = threatAssessment.severity;

          return (
            <Accordion.Item key={scenario.scenarioId} value={scenario.scenarioId}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="md">
                    <IconAlertTriangle size={20} color={getThreatLevelColor(threatLevel)} />
                    <Stack gap={4}>
                      <Text fw={600}>{scenario.name || 'Unnamed Scenario'}</Text>
                      <Group gap="xs">
                        {scenario.mappedAgents && scenario.mappedAgents.length > 0 && (
                          <Text size="xs" c="dimmed">
                            Threat Agents: {scenario.mappedAgents.join(', ')}
                          </Text>
                        )}
                      </Group>
                    </Stack>
                  </Group>

                  <Group gap="xs" wrap="nowrap">
                    <Badge
                      variant="filled"
                      color={getThreatLevelColor(threatLevel)}
                      size="sm"
                    >
                      {threatLevel}
                    </Badge>
                    <Badge variant="outline" color="gray" size="sm">
                      {controls.length} {controls.length === 1 ? 'Control' : 'Controls'}
                    </Badge>
                  </Group>
                </Group>
              </Accordion.Control>

              <Accordion.Panel>
                <Stack gap="md">
                  {/* Threat Assessment */}
                  {(vulnerabilityLevel || assetValue || likelihood || severity) && (
                    <Paper withBorder radius="md" p="md" bg="gray.0">
                      <Stack gap="sm">
                        <Group gap="xs">
                          <IconInfoCircle size={16} />
                          <Text size="sm" fw={600}>Threat Assessment</Text>
                        </Group>
                        <Group gap="md" wrap="wrap">
                          {vulnerabilityLevel && (
                            <Group gap={4}>
                              <Text size="xs" c="dimmed">Vulnerability:</Text>
                              <Badge size="sm" variant="light" color={getThreatLevelColor(vulnerabilityLevel)}>
                                {vulnerabilityLevel}
                              </Badge>
                            </Group>
                          )}
                          {assetValue && (
                            <Group gap={4}>
                              <Text size="xs" c="dimmed">Asset Value:</Text>
                              <Badge size="sm" variant="light" color={getThreatLevelColor(assetValue)}>
                                {assetValue}
                              </Badge>
                            </Group>
                          )}
                          {likelihood && (
                            <Group gap={4}>
                              <Text size="xs" c="dimmed">Likelihood:</Text>
                              <Badge size="sm" variant="light" color={getThreatLevelColor(likelihood)}>
                                {likelihood}
                              </Badge>
                            </Group>
                          )}
                          {severity && (
                            <Group gap={4}>
                              <Text size="xs" c="dimmed">Severity:</Text>
                              <Badge size="sm" variant="light" color={getThreatLevelColor(severity)}>
                                {severity}
                              </Badge>
                            </Group>
                          )}
                        </Group>
                        {threatAssessment.rationale && (
                          <Text size="xs" c="dimmed" mt="xs">
                            {threatAssessment.rationale}
                          </Text>
                        )}
                      </Stack>
                    </Paper>
                  )}

                  {/* Attack Surface */}
                  {attackSurface.category && (
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>Attack Surface</Text>
                      <Group gap="xs">
                        <Badge variant="light" color="orange" size="sm">
                          {attackSurface.category}
                        </Badge>
                        {attackSurface.vulnerabilities && Array.isArray(attackSurface.vulnerabilities) && (
                          attackSurface.vulnerabilities.map((vuln, idx) => (
                            <Badge key={idx} variant="outline" color="red" size="sm">
                              {vuln}
                            </Badge>
                          ))
                        )}
                      </Group>
                    </Stack>
                  )}

                  {/* Impact Areas */}
                  {impact && Object.keys(impact).length > 0 && (
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>Impact Areas</Text>
                      <Group gap="xs">
                        {Object.entries(impact).map(([area, level]) => (
                          <Badge
                            key={area}
                            variant="light"
                            color={getThreatLevelColor(level)}
                            size="sm"
                          >
                            {area}: {level}
                          </Badge>
                        ))}
                      </Group>
                    </Stack>
                  )}

                  {/* Security Controls */}
                  {controls.length > 0 ? (
                    <Stack gap="xs">
                      <Group gap="xs">
                        <IconChecklist size={16} />
                        <Text size="sm" fw={600}>Security Controls ({controls.length})</Text>
                      </Group>
                      <List
                        spacing="xs"
                        size="sm"
                        icon={
                          <ThemeIcon color="green" size={20} radius="xl">
                            <IconShield size={12} />
                          </ThemeIcon>
                        }
                      >
                        {controls.map((control) => (
                          <List.Item key={control.controlId}>
                            <Group gap="xs">
                              <Text size="sm">{control.name}</Text>
                              {control.status && (
                                <Badge size="xs" variant="outline" color="gray">
                                  {control.status}
                                </Badge>
                              )}
                            </Group>
                          </List.Item>
                        ))}
                      </List>
                    </Stack>
                  ) : (
                    <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                      No security controls generated for this scenario yet.
                    </Alert>
                  )}

                  {/* Scenario Attributes */}
                  {scenario.attributes && Object.keys(scenario.attributes).length > 0 && (
                    <Box>
                      <Text size="sm" fw={600} mb="xs">Attributes</Text>
                      <Paper withBorder radius="md" p="sm" bg="gray.0">
                        <Stack gap="xs">
                          {Object.entries(scenario.attributes).map(([key, value]) => (
                            <Group key={key} gap="xs" wrap="wrap">
                              <Text size="xs" c="dimmed" fw={500} style={{ minWidth: 80 }}>
                                {key}:
                              </Text>
                              {Array.isArray(value) ? (
                                <Group gap={4}>
                                  {value.map((v, idx) => (
                                    <Badge key={idx} size="xs" variant="outline" color="gray">
                                      {v}
                                    </Badge>
                                  ))}
                                </Group>
                              ) : (
                                <Text size="xs">{value}</Text>
                              )}
                            </Group>
                          ))}
                        </Stack>
                      </Paper>
                    </Box>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Stack>
  );
}
